import useAuth from "hooks/useAuth";
import useList from "hooks/useList";
import useRouter from "common/hooks/use-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Organization, Plan, Subscription } from "types";
import type { TableColumn } from "common/components/DataTable/DataTable";
import CrudList from "common/components/Crud/CrudList";
import type { CrudHeaderAction } from "common/components/Crud/components/CrudHeader";
import type { CrudFilter } from "common/components/Crud/components/CrudFilters";
import CrudEdit from "common/components/Crud/CrudEdit";
import Icon, { IconNames } from "components/Icon/Icon";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import OrganizationForm from "./OrganizationForm";
import { ProductNameLabel } from "pages/Products/productUtils";
import { TbGauge } from "react-icons/tb";

const SUBSCRIPTION_ICONS: IconNames[] = ["BsStar", "BsStarHalf", "BsStarFill"];

const interpolateColor = (t: number) => {
  const r = Math.round(0x00 + t * (0x98 - 0x00));
  const g = Math.round(0x9f + t * (0xcc - 0x9f));
  const b = Math.round(0xed + t * (0x33 - 0xed));
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

const SECONDARY_COLOR = "#009FED";
const TERNARY_COLOR = "#98CC33";

function SidebarOrganizationForm({ ...props }: any) {
  return (
    <CrudEdit
      collectionName="organizations"
      Form={OrganizationForm}
      {...props}
    />
  );
}

function Organizations() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { navigate } = useRouter();
  const { items: subscriptions } = useList<Subscription>("subscriptions");

  const canManageOrganizations =
    currentUser?.isSuperadmin || currentUser?.isAdmin;

  const subscriptionMeta = useMemo(() => {
    const list = subscriptions ?? [];
    return list.reduce((acc, s, index) => {
      const ratio = list.length > 1 ? index / (list.length - 1) : 0;
      const color =
        index === 0
          ? SECONDARY_COLOR
          : index === list.length - 1
          ? TERNARY_COLOR
          : interpolateColor(ratio);
      acc[s.id] = { icon: SUBSCRIPTION_ICONS[index] ?? "BsStarFill", color };
      return acc;
    }, {} as Record<number, { icon: IconNames; color: string }>);
  }, [subscriptions]);

  const columns: TableColumn<Organization>[] = useMemo(
    () => [
      {
        field: "name",
        header: t("organizations.name"),
        sortable: true,
        renderType: "bold",
      },
      {
        field: "logo",
        header: t("organizations.logo"),
        renderType: "image",
      },
      {
        field: "products",
        header: t("organizations.products"),
        customRender: (row: Organization) => {
          const products = row.products ?? [];
          if (!products.length) return <span>-</span>;
          const visible = products.slice(0, 3);
          const rest = products.slice(3);
          return (
            <div className="flex flex-col gap-1">
              {visible.map((p) => (
                <div key={p.id} className="flex items-center gap-1.5">
                  {p.image?.url ? (
                    <img
                      src={ImageFromStrapiMedia(p.image).uri}
                      alt={p.name}
                      className="w-5 h-5 rounded object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded bg-slate-200 shrink-0" />
                  )}

                  <ProductNameLabel key={p.id} product={p} />
                </div>
              ))}
              {rest.length > 0 && (
                <TooltipWrapper
                  content={
                    <div className="flex flex-col gap-0.5">
                      {rest.map((p) => (
                        <ProductNameLabel key={p.id} product={p} />
                      ))}
                    </div>
                  }
                >
                  <span className="text-xs text-slate-400 cursor-default">
                    +{rest.length} {t("common.more")}
                  </span>
                </TooltipWrapper>
              )}
            </div>
          );
        },
      },
      {
        field: "plans",
        header: t("organizations.plans"),
        customRender: (row: Organization) => {
          const plans = row.plans ?? [];
          if (!plans.length) return <span>-</span>;
          const visible = plans.slice(0, 3);
          const rest = plans.slice(3);
          return (
            <div className="flex flex-col gap-1">
              {visible.map((p) => (
                <div key={p.id} className="flex items-center gap-1.5">
                  <TbGauge className="text-primary-800 size-5 rounded object-cover shrink-0" />

                  <span>{p.name}</span>
                </div>
              ))}
              {rest.length > 0 && (
                <TooltipWrapper
                  content={
                    <div className="flex flex-col gap-0.5">
                      {rest.map((p) => (
                        <span key={p.id}>{p.name}</span>
                      ))}
                    </div>
                  }
                >
                  <span className="text-xs text-slate-400 cursor-default">
                    +{rest.length} {t("common.more")}
                  </span>
                </TooltipWrapper>
              )}
            </div>
          );
        },
      },
      {
        field: "subscription",
        header: t("organizations.subscription"),
        customRender: (row: Organization) => {
          if (!row.subscription) return <span>-</span>;
          const meta = subscriptionMeta[row.subscription.id];
          const color = meta?.color ?? "#718096";
          const icon = meta?.icon ?? "BsStar";
          return (
            <div
              className="inline-flex items-center gap-1.5 rounded px-2 py-1"
              style={{ backgroundColor: color }}
            >
              <Icon name={icon} className="w-3.5 h-3.5 text-white" />
              <span className="text-xs font-medium text-white">
                {row.subscription.name}
              </span>
            </div>
          );
        },
      },
    ],
    [t, subscriptionMeta],
  );

  const actions: CrudHeaderAction[] = useMemo(() => {
    return [
      {
        title: t("common.new"),
        icon: "PlusCircleIconSolid",
        action: "openSidebar",
        type: "white",
        disabled: !canManageOrganizations,
        className: "flex flex-row-reverse",
      },
    ];
  }, [canManageOrganizations, t]);

  const filters: CrudFilter[] = useMemo(() => {
    return [
      {
        name: "name$containsi",
        label: t("organizations.name"),
        renderType: "input",
        icon: "MagnifyingGlassIcon",
      },
    ];
  }, [t]);

  return (
    <CrudList
      collectionName="organizations"
      columns={columns}
      headerActions={actions}
      filters={filters}
      defaultSort="name:ASC"
      canDelete={canManageOrganizations}
      canEdit={canManageOrganizations}
      formMode="sidebar"
      SidebarFormComponent={SidebarOrganizationForm}
      onNavigate={(id: any) => navigate(`/organizations/${id}`)}
      actionsHeader=""
      className="pt-2 px-4"
    />
  );
}

export default Organizations;
